import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  DotsThreeIcon,
  GithubLogoIcon,
  PencilSimpleIcon,
  PlugsIcon,
  PlusIcon,
  SparkleIcon,
  TrashIcon,
} from '@phosphor-icons/react';

import {
  createConnector,
  deleteConnector,
  discoverConnectorTools,
  discoverDraftConnectorTools,
  MAX_ENABLED_TOOLS,
  updateConnector,
  useConnectors,
  type Connector,
  type DiscoveredTool,
} from '@/api-models/connectors';
import { useAuth } from '@/contexts/auth-context';
import { useSpace } from '@/contexts/space-context';
import { ProviderCard } from '@/features/settings/components/ProviderCard';
import { StatusPill } from '@/features/settings/components/StatusPill';
import { Alert } from '@/ui/Alert';
import { Badge } from '@/ui/Badge';
import { Button, IconButton } from '@/ui/button/Button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/ui/DropdownMenu';
import { Input } from '@/ui/Input';
import { Modal } from '@/ui/Modal';
import { SegmentedControl } from '@/ui/SegmentedControl';
import { Spinner } from '@/ui/Spinner';
import { Table, TableBody, TableCell, TableHead, TableHeaderCell, TableRow } from '@/ui/Table';
import { formatTimeAgo } from '@/utils/format';

type Scope = 'personal' | 'team';

/** A header row in the editor. Kept as a list so header *names* stay editable. */
interface HeaderDraft {
  name: string;
  value: string;
}

/**
 * Settings → Connectors.
 *
 * MCP servers the agent can call tools on. Every call is proxied through our API, so the
 * credentials entered here are encrypted at rest and never reach a sandbox or the model.
 *
 * Connectors live at either a personal or a team level — both map onto a real team (every user
 * has a personal team), so one segmented toggle switches the scope, exactly as playbooks do.
 */
export function ConnectorsSection() {
  const { me } = useAuth();
  const { activeTeam } = useSpace();

  const teamSpace = activeTeam && !activeTeam.is_personal ? activeTeam : null;
  const [scope, setScope] = useState<Scope>(teamSpace ? 'team' : 'personal');

  // Switching spaces changes which scopes exist; follow the space.
  useEffect(() => {
    setScope(teamSpace ? 'team' : 'personal');
  }, [teamSpace]);

  const effectiveTeamId = scope === 'team' && teamSpace ? teamSpace.id : me.personalTeamId;
  const { connectors, mutate, isLoading } = useConnectors(effectiveTeamId);

  const [editor, setEditor] = useState<{ connector: Connector | null } | null>(null);

  async function handleDelete(connector: Connector) {
    try {
      await deleteConnector(effectiveTeamId, connector.id);
      await mutate();
      toast.success(`Removed "${connector.name}"`);
    } catch {
      toast.error('Could not remove connector');
    }
  }

  return (
    <div className="flex flex-col gap-7">
      {/* Header */}
      <div className="flex items-start justify-between gap-6">
        <div className="flex flex-col">
          <h1 className="page-title">Connectors</h1>
          <p className="page-subtitle">
            Give agents tools from outside the repository. Calls are proxied through Zooid, so credentials stay on our
            servers and never reach the sandbox or the model.
          </p>
        </div>
        <Button
          type="button"
          variant="primary"
          size={9}
          onClick={() => setEditor({ connector: null })}
          iconLeft={<PlusIcon size={16} weight="bold" />}
        >
          Add connector
        </Button>
      </div>

      {/* Scope controls — the Team scope only exists inside a team space. */}
      {teamSpace && (
        <div className="flex items-center gap-3">
          <SegmentedControl<Scope>
            value={scope}
            onChange={setScope}
            ariaLabel="Connector scope"
            options={[
              { value: 'personal', label: 'Personal' },
              { value: 'team', label: 'Team' },
            ]}
          />
          <span className="text-xs text-fg-tertiary">
            {scope === 'team'
              ? `Available to everyone in ${teamSpace.name}.`
              : 'Only your own agent sessions can use these.'}
          </span>
        </div>
      )}

      {/* Built-ins. Both are synthesised server-side for every space — nothing to configure. */}
      <div className="flex flex-col gap-2.5">
        <ProviderCard
          icon={<SparkleIcon size={18} />}
          name="Zooid"
          badge={<StatusPill tone="success">Always on</StatusPill>}
          description="Lets the agent manage your playbooks. Reading is automatic; creating, changing or deleting one asks you first."
        />
        <ProviderCard
          icon={<GithubLogoIcon size={18} />}
          name="Repository"
          badge={<StatusPill tone="success">Always on</StatusPill>}
          description="Lets the agent open, update, merge and close pull requests for the repo it is working in, and read their CI checks, reviews and comments. Uses the GitHub account you signed in with — reading is automatic, anything that writes asks you first."
        />
      </div>

      {/* User-defined connectors */}
      <div className="overflow-hidden rounded-xl border border-line bg-surface-1">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell>Connector</TableHeaderCell>
              <TableHeaderCell className="w-32">Namespace</TableHeaderCell>
              <TableHeaderCell className="w-24">Tools</TableHeaderCell>
              <TableHeaderCell className="w-40">Updated</TableHeaderCell>
              <TableHeaderCell className="w-12" />
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && connectors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-fg-tertiary">
                  <span className="inline-flex items-center gap-2">
                    <Spinner size={4} /> Loading connectors…
                  </span>
                </TableCell>
              </TableRow>
            ) : connectors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-fg-tertiary">
                  No connectors yet. Add one to let agents reach a service outside this repository.
                </TableCell>
              </TableRow>
            ) : (
              connectors.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <button
                      type="button"
                      className="flex min-w-0 items-center gap-2.5 text-left"
                      onClick={() => setEditor({ connector: c })}
                    >
                      <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-surface-3 text-fg-secondary">
                        <PlugsIcon size={16} />
                      </span>
                      <span className="flex min-w-0 flex-col">
                        <span className="truncate text-sm font-semibold text-fg">{c.name}</span>
                        {c.endpoint && <span className="truncate text-2xs text-fg-tertiary">{c.endpoint}</span>}
                      </span>
                    </button>
                  </TableCell>
                  <TableCell>
                    <code className="rounded bg-surface-3 px-1.5 py-0.5 text-xs text-fg-secondary">{c.slug}</code>
                  </TableCell>
                  <TableCell>
                    <Badge variant={c.enabled && c.enabled_tools.length > 0 ? 'neutral' : 'warning'} size="md">
                      {c.enabled ? c.enabled_tools.length : 'Off'}
                    </Badge>
                  </TableCell>
                  <TableCell className="font-medium text-fg-secondary">{formatTimeAgo(c.updated_at)}</TableCell>
                  <TableCell>
                    <div className="flex justify-end">
                      <ConnectorMenu onEdit={() => setEditor({ connector: c })} onDelete={() => handleDelete(c)} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {editor && (
        <ConnectorEditorModal
          teamId={effectiveTeamId}
          connector={editor.connector}
          onClose={() => setEditor(null)}
          onSaved={mutate}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Building blocks
// ---------------------------------------------------------------------------

function ConnectorMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconButton size={8} aria-label="Connector actions">
          <DotsThreeIcon size={18} weight="bold" />
        </IconButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <PencilSimpleIcon size={15} />
          Edit
        </DropdownMenuItem>
        <DropdownMenuItem destructive onSelect={onDelete}>
          <TrashIcon size={15} />
          Remove
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Derive a namespace slug from a display name, so the user rarely types one by hand. */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '')
    .slice(0, 32);
}

function ConnectorEditorModal({
  teamId,
  connector,
  onClose,
  onSaved,
}: {
  teamId: string;
  connector: Connector | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = connector !== null;

  const [name, setName] = useState(connector?.name ?? '');
  const [slug, setSlug] = useState(connector?.slug ?? '');
  const [slugEdited, setSlugEdited] = useState(isEdit);
  const [endpoint, setEndpoint] = useState(connector?.endpoint ?? '');
  const [enabled, setEnabled] = useState(connector?.enabled ?? true);

  // Values are write-only: an existing connector starts with its header *names* and blank
  // values, and blank means "leave what's stored alone".
  const [headers, setHeaders] = useState<HeaderDraft[]>(
    connector?.auth_header_names.length
      ? connector.auth_header_names.map((n) => ({ name: n, value: '' }))
      : [{ name: 'Authorization', value: '' }],
  );

  const [discovered, setDiscovered] = useState<DiscoveredTool[] | null>(null);
  const [selected, setSelected] = useState<string[]>(connector?.enabled_tools ?? []);
  const [testing, setTesting] = useState(false);
  const [busy, setBusy] = useState(false);

  const effectiveSlug = slugEdited ? slug : slugify(name);

  function headerMap(): Record<string, string> {
    const map: Record<string, string> = {};
    for (const h of headers) {
      const key = h.name.trim();
      if (key && h.value.trim()) map[key] = h.value.trim();
    }
    return map;
  }

  /**
   * Only send `auth_headers` when the user actually typed a value. Sending `{}` would be an
   * explicit "clear the credentials", which is not what an edit that skipped the field means.
   */
  function headersForSubmit(): Record<string, string> | undefined {
    const map = headerMap();
    if (Object.keys(map).length > 0) return map;
    return isEdit ? undefined : {};
  }

  async function handleTest() {
    setTesting(true);
    try {
      const tools =
        isEdit && connector
          ? await discoverConnectorTools(teamId, connector.id)
          : await discoverDraftConnectorTools(teamId, {
              slug: effectiveSlug || 'draft',
              name: name.trim() || 'Draft',
              endpoint: endpoint.trim(),
              auth_headers: headerMap(),
              enabled_tools: [],
              enabled: true,
            });
      setDiscovered(tools);
      // Keep only selections the server still advertises — anything else could not be called.
      setSelected((prev) => prev.filter((n) => tools.some((t) => t.name === n)));
      toast.success(tools.length === 1 ? 'Found 1 tool' : `Found ${tools.length} tools`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not reach that server');
    } finally {
      setTesting(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      if (isEdit && connector) {
        await updateConnector(teamId, connector.id, {
          name: name.trim(),
          endpoint: endpoint.trim(),
          auth_headers: headersForSubmit(),
          enabled_tools: selected,
          enabled,
        });
        toast.success('Connector updated');
      } else {
        await createConnector(teamId, {
          slug: effectiveSlug,
          name: name.trim(),
          endpoint: endpoint.trim(),
          auth_headers: headersForSubmit(),
          enabled_tools: selected,
          enabled,
        });
        toast.success('Connector added');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save connector');
    } finally {
      setBusy(false);
    }
  }

  const canSubmit = name.trim().length > 0 && endpoint.trim().length > 0 && !!effectiveSlug;

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={isEdit ? `Edit ${connector.name}` : 'Add connector'}
      description="Point Zooid at an MCP server, then choose which of its tools agents may use."
      maxWidth="max-w-2xl"
    >
      <form onSubmit={submit} className="flex flex-col gap-4 px-6 pb-6">
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-1.5">
            <label htmlFor="mc-name" className="text-xs font-medium text-fg">
              Name
            </label>
            <Input
              id="mc-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Acme CRM"
              autoFocus
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <label htmlFor="mc-slug" className="text-xs font-medium text-fg">
              Namespace
            </label>
            <Input
              id="mc-slug"
              value={effectiveSlug}
              onChange={(e) => {
                setSlugEdited(true);
                setSlug(e.target.value);
              }}
              disabled={isEdit}
              className="font-mono"
              placeholder="acme_crm"
            />
            <span className="text-2xs leading-3.5 text-fg-tertiary">
              {isEdit
                ? 'Fixed after creation — agents already know its tools by this name.'
                : `Tools appear as mcp__${effectiveSlug || 'namespace'}__<tool>.`}
            </span>
          </div>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="mc-endpoint" className="text-xs font-medium text-fg">
            Server URL
          </label>
          <Input
            id="mc-endpoint"
            value={endpoint}
            onChange={(e) => setEndpoint(e.target.value)}
            placeholder="https://example.com/mcp"
            className="font-mono"
          />
          <span className="text-2xs leading-3.5 text-fg-tertiary">
            Must be https — credentials travel on this request.
          </span>
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-fg">
            Auth headers <span className="font-normal text-fg-tertiary">(optional)</span>
          </span>
          <div className="flex flex-col gap-2">
            {headers.map((h, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  value={h.name}
                  onChange={(e) =>
                    setHeaders((prev) => prev.map((row, j) => (j === i ? { ...row, name: e.target.value } : row)))
                  }
                  placeholder="Authorization"
                  className="w-56 font-mono"
                  aria-label="Header name"
                />
                <Input
                  type="password"
                  value={h.value}
                  onChange={(e) =>
                    setHeaders((prev) => prev.map((row, j) => (j === i ? { ...row, value: e.target.value } : row)))
                  }
                  placeholder={
                    connector?.auth_header_names.includes(h.name) ? 'Enter a new value to replace' : 'Bearer …'
                  }
                  className="flex-1 font-mono"
                  autoComplete="new-password"
                  aria-label="Header value"
                />
                <IconButton
                  size={8}
                  type="button"
                  aria-label="Remove header"
                  onClick={() => setHeaders((prev) => prev.filter((_, j) => j !== i))}
                >
                  <TrashIcon size={15} />
                </IconButton>
              </div>
            ))}
          </div>
          <div>
            <Button
              type="button"
              variant="ghost"
              size={8}
              onClick={() => setHeaders((prev) => [...prev, { name: '', value: '' }])}
              iconLeft={<PlusIcon size={14} weight="bold" />}
            >
              Add header
            </Button>
          </div>
          <span className="text-2xs leading-3.5 text-fg-tertiary">
            Encrypted at rest with AES-256-GCM and never displayed back. Leave a value blank to keep the one already
            stored.
          </span>
        </div>

        {/* Tool picker */}
        <div className="flex flex-col gap-2 rounded-lg border border-line bg-surface-2 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex flex-col">
              <span className="text-xs font-medium text-fg">Tools</span>
              <span className="text-2xs leading-3.5 text-fg-tertiary">
                Each enabled tool costs context on every model call, so pick only what agents need.
              </span>
            </div>
            <Button
              type="button"
              variant="secondary"
              size={8}
              onClick={handleTest}
              isLoading={testing}
              disabled={!canSubmit}
            >
              {discovered ? 'Refresh' : 'Test connection'}
            </Button>
          </div>

          {discovered === null ? (
            <p className="text-2xs text-fg-tertiary">
              {selected.length > 0
                ? `${selected.length} tool${selected.length === 1 ? '' : 's'} enabled. Test the connection to change the selection.`
                : 'Test the connection to see what this server offers.'}
            </p>
          ) : discovered.length === 0 ? (
            <p className="text-2xs text-fg-tertiary">That server advertises no tools.</p>
          ) : (
            <div className="flex max-h-56 flex-col gap-1 overflow-y-auto">
              {discovered.map((tool) => {
                const checked = selected.includes(tool.name);
                const atCap = !checked && selected.length >= MAX_ENABLED_TOOLS;
                return (
                  <label
                    key={tool.name}
                    className={`flex items-start gap-2.5 rounded-md px-2 py-1.5 ${
                      atCap ? 'opacity-50' : 'hover:bg-surface-3'
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="mt-0.5"
                      checked={checked}
                      disabled={atCap}
                      onChange={(e) =>
                        setSelected((prev) =>
                          e.target.checked ? [...prev, tool.name] : prev.filter((n) => n !== tool.name),
                        )
                      }
                    />
                    <span className="flex min-w-0 flex-col">
                      <span className="flex items-center gap-2">
                        <code className="text-xs text-fg">{tool.name}</code>
                        <Badge variant={tool.read_only ? 'neutral' : 'warning'} size="sm">
                          {tool.read_only ? 'Read' : 'Write'}
                        </Badge>
                      </span>
                      {tool.description && (
                        <span className="truncate text-2xs text-fg-tertiary">{tool.description}</span>
                      )}
                    </span>
                  </label>
                );
              })}
            </div>
          )}

          {selected.length >= MAX_ENABLED_TOOLS && (
            <Alert variant="warning">At most {MAX_ENABLED_TOOLS} tools can be enabled per connector.</Alert>
          )}
        </div>

        <Alert variant="info">
          Agents run read-only tools straight away. Anything that writes stops and asks you first — once per tool, per
          session.
        </Alert>

        {isEdit && (
          <label className="flex items-center gap-2 text-xs text-fg">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            Enabled
          </label>
        )}

        <div className="mt-1 flex justify-end gap-2">
          <Button type="button" variant="secondary" size={9} onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size={9} disabled={!canSubmit} isLoading={busy}>
            {isEdit ? 'Save changes' : 'Add connector'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
